import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Drawer, Input, Pagination, Select, Spin, notification } from 'antd';
import { collection, orderBy, query } from 'firebase/firestore';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import ActionButton from '@src/components/Buttons/ActionButton';
import FilterContainer from '@src/components/FilterContainer';
import MemberAvatar from '@src/components/MemberList/MemberAvatar';
import { ResponsiveTable } from '@src/components/Table';
import type { ResponsiveTableColumns } from '@src/components/Table';
import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import type {
  IWorkJourneyMatrixUser,
  IWorkJourneyReplacementCandidate,
} from '@src/models/interfaces/workJourneyManagement.interface';
import { useFirestoreRealtimeBatched } from '@src/packages/utils';
import { ALLOWED_ROLES } from '@src/packages/utils/access';
import {
  needsJurisdictionIdentifier,
  formatAttendanceIdentifierDisplay,
  getOrganizationCountry,
  type JurisdictionIdentifier,
  type OrganizationCountrySource,
} from '@src/packages/utils/nationalId';
import JurisdictionIdPromptModal from '@src/packages/workJourney/components/JurisdictionIdPromptModal';
import { CoverCurrentJourneyLabel } from '@src/packages/workJourney/screens/WorkJourneyManagement/components/CoverPositionDrawers/CoverCurrentJourneyLabel';
import { persistOrganizationUserJurisdictionId } from '@src/packages/workJourney/utils/persistOrganizationUserJurisdictionId';
import UserRepository from '@src/repositories/UserRepository';
import WorkJourneyManagementService from '@src/services/WorkJourneyManagementService';
import IntlMessages from '@src/util/IntlMessages';

import drawerStyles from '../matrixSelectionDrawers.module.less';

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

type UserJobOption = {
  id: string;
  name: string;
};

export type WorkJourneyMatrixReplaceDrawerProps = {
  open: boolean;
  replacedUser: IWorkJourneyMatrixUser | null;
  /** Plantilla member ids (+ replaced user) excluded from ES candidates. */
  excludeUserIds?: string[];
  submitting?: boolean;
  onClose: () => void;
  onContinue: (candidate: IWorkJourneyReplacementCandidate) => Promise<void> | void;
};

export const WorkJourneyMatrixReplaceDrawer: FC<WorkJourneyMatrixReplaceDrawerProps> = ({
  open,
  replacedUser,
  excludeUserIds,
  submitting = false,
  onClose,
  onContinue,
}) => {
  const intl = useIntl();
  const serviceRef = useRef(new WorkJourneyManagementService());
  const userRepositoryRef = useRef(new UserRepository());
  const { organizationId, organizationCountry } = useSelector(
    ({
      organizations,
    }: {
      organizations: {
        organization: OrganizationCountrySource & { id?: string };
      };
    }) => ({
      organizationId: organizations.organization?.id,
      organizationCountry: getOrganizationCountry(organizations.organization),
    }),
  );

  const [users, setUsers] = useState<IWorkJourneyReplacementCandidate[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [jobId, setJobId] = useState<string | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedUser, setSelectedUser] = useState<IWorkJourneyReplacementCandidate | null>(null);
  const [pendingJurisdictionUser, setPendingJurisdictionUser] =
    useState<IWorkJourneyReplacementCandidate | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const resolvedExcludeUserIds = useMemo(() => {
    if (excludeUserIds?.length) {
      return excludeUserIds.filter(Boolean);
    }
    return replacedUser?.id ? [replacedUser.id] : [];
  }, [excludeUserIds, replacedUser?.id]);

  const excludeKey = useMemo(
    () => resolvedExcludeUserIds.join(','),
    [resolvedExcludeUserIds],
  );

  useEffect(() => {
    if (!open) {
      setSearch('');
      setDebouncedSearch('');
      setJobId(undefined);
      setCurrentPage(1);
      setSelectedUser(null);
      setPendingJurisdictionUser(null);
      setUsers([]);
      setTotal(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !organizationId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await serviceRef.current.listReplacementCandidates({
          organizationId,
          search: debouncedSearch || undefined,
          jobId,
          excludeUserIds: excludeKey ? excludeKey.split(',') : [],
          from: (currentPage - 1) * pageSize,
          pageSize,
        });
        if (cancelled) return;
        setUsers(response.users);
        setTotal(response.total);
      } catch (error) {
        console.error('Error loading replacement candidates:', error);
        if (!cancelled) {
          setUsers([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    organizationId,
    debouncedSearch,
    jobId,
    currentPage,
    pageSize,
    excludeKey,
  ]);

  const jobsQueryList = useMemo(() => {
    if (!organizationId || !open) return [];
    return [
      query(
        collection(db, FSCollections.ORGANIZATIONS, organizationId, FSCollections.USER_JOBS),
        orderBy('name'),
      ),
    ];
  }, [organizationId, open]);

  const { data: jobs = [] } = useFirestoreRealtimeBatched(jobsQueryList, [organizationId, open]);
  const jobOptions = useMemo(
    () =>
      (jobs as UserJobOption[]).map(job => ({
        label: job.name,
        value: job.id,
      })),
    [jobs],
  );

  const handleSelectUser = useCallback(
    async (user: IWorkJourneyReplacementCandidate) => {
      if (!organizationId) {
        return;
      }
      const orgUser = await userRepositoryRef.current.getUser(organizationId, user.id);
      const personalIdCard = orgUser?.idCard || user.idCard || '';
      const existingJurisdictionId = orgUser?.jurisdictionId ?? user.jurisdictionId;
      if (
        needsJurisdictionIdentifier(
          organizationCountry,
          personalIdCard,
          existingJurisdictionId,
          orgUser?.address?.country,
        )
      ) {
        setPendingJurisdictionUser({
          ...user,
          idCard: personalIdCard,
          jurisdictionId: existingJurisdictionId,
          address: orgUser?.address ?? user.address,
        });
        return;
      }
      setSelectedUser(user);
    },
    [organizationCountry, organizationId],
  );

  const handleConfirmJurisdictionId = useCallback(
    async (identifier: JurisdictionIdentifier) => {
      if (!organizationId || !pendingJurisdictionUser) {
        return;
      }
      try {
        const orgUser = await userRepositoryRef.current.getUser(
          organizationId,
          pendingJurisdictionUser.id,
        );
        if (!orgUser) {
          throw new Error('Organization user not found');
        }
        await persistOrganizationUserJurisdictionId(organizationId, orgUser, identifier);
        setSelectedUser({ ...pendingJurisdictionUser, jurisdictionId: identifier });
        setPendingJurisdictionUser(null);
      } catch (error) {
        console.error('Error saving jurisdiction identifier:', error);
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
        });
      }
    },
    [intl, organizationId, pendingJurisdictionUser],
  );

  const columns: ResponsiveTableColumns<IWorkJourneyReplacementCandidate> = [
    {
      title: intl.formatMessage({ id: 'workJourney.management.cover.columns.user' }),
      key: 'user',
      render: (user: IWorkJourneyReplacementCandidate) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MemberAvatar size={32} user={user} />
          <span>{user.displayName}</span>
        </div>
      ),
    },
    {
      title: intl.formatMessage({ id: 'workJourney.management.cover.columns.idCard' }),
      key: 'idCard',
      render: (user: IWorkJourneyReplacementCandidate) =>
        formatAttendanceIdentifierDisplay(organizationCountry, user) || '—',
    },
    {
      title: intl.formatMessage({
        id: 'workJourney.management.cover.replace.columns.currentJourney',
      }),
      key: 'journey',
      render: (user: IWorkJourneyReplacementCandidate) => (
        <CoverCurrentJourneyLabel
          journeyName={user.currentWorkJourney?.workJourneyConfigName}
        />
      ),
    },
    {
      title: '',
      key: 'action',
      columnType: 'actions',
      align: 'right',
      render: (user: IWorkJourneyReplacementCandidate) => {
        const isSelected = selectedUser?.id === user.id;
        return (
          <ActionButton
            label={intl.formatMessage({
              id: isSelected
                ? 'workJourney.management.cover.replace.selected'
                : 'workJourney.management.cover.replace.select',
            })}
            type={isSelected ? 'primary' : 'default'}
            color="primary"
            size="medium"
            onClick={() => {
              void handleSelectUser(user);
            }}
            allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}
            disabledScaling
          />
        );
      },
    },
  ];

  const handleContinue = useCallback(async () => {
    if (!selectedUser) return;
    await onContinue(selectedUser);
  }, [onContinue, selectedUser]);

  return (
    <Drawer
      open={open}
      width={720}
      title={null}
      closable={false}
      footer={null}
      destroyOnClose
      onClose={onClose}
    >
      <BoxContainer>
        <BoxContainer content fixed bottomBorder>
          <FilterContainer
            goBack={onClose}
            title={
              <Title.Header
                value={
                  <IntlMessages id="workJourney.management.matrix.replaceDrawer.title" />
                }
              />
            }
            actionButtons={[
              {
                label: intl.formatMessage({
                  id: 'workJourney.management.matrix.replaceDrawer.continue',
                }),
                type: 'primary',
                action: () => {
                  void handleContinue();
                },
                disabled: submitting || !selectedUser,
              },
            ]}
          />
        </BoxContainer>

        <BoxContainer content>
          <div className={drawerStyles.toolbar}>
            <Input.Search
              className={drawerStyles.searchInput}
              allowClear
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={intl.formatMessage({
                id: 'workJourney.management.cover.replace.searchPlaceholder',
              })}
            />
            <Select
              allowClear
              className={drawerStyles.jobSelect}
              value={jobId}
              onChange={value => {
                setJobId(value);
                setCurrentPage(1);
              }}
              placeholder={intl.formatMessage({
                id: 'workJourney.management.cover.replace.jobPlaceholder',
              })}
              options={jobOptions}
            />
          </div>

          <Spin spinning={loading || submitting}>
            <ResponsiveTable dataSource={users} columns={columns} />
            <div className="gx-mt-3 gx-text-right">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={total}
                showSizeChanger
                onChange={(page, size) => {
                  setCurrentPage(page);
                  if (size) setPageSize(size);
                }}
              />
            </div>
          </Spin>
        </BoxContainer>
      </BoxContainer>
      <JurisdictionIdPromptModal
        open={!!pendingJurisdictionUser}
        organizationCountry={organizationCountry}
        personalCountry={pendingJurisdictionUser?.address?.country}
        personalIdCard={pendingJurisdictionUser?.idCard}
        userName={pendingJurisdictionUser?.displayName}
        onCancel={() => setPendingJurisdictionUser(null)}
        onConfirm={handleConfirmJurisdictionId}
      />
    </Drawer>
  );
};
